import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from './ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import {
    Plus,
    Trash,
    Pencil,
    FileText,
    Warning,
    ArrowUp,
    ArrowDown,
    Copy,
    Robot,
    Sparkle
} from '@phosphor-icons/react';

// Document type categories
const DOCUMENT_CATEGORIES = [
    { value: 'contrattuale', label: 'Contrattuale' },
    { value: 'tecnico', label: 'Tecnico' },
    { value: 'amministrativo', label: 'Amministrativo' },
    { value: 'legale', label: 'Legale' },
    { value: 'altro', label: 'Altro' }
];

// Common document types for suggestions
const COMMON_DOC_TYPES = [
    { code: 'MODULO_ADESIONE', label: 'Modulo di Adesione', category: 'contrattuale', supports_extraction: true, description: 'Modulo contrattuale con estrazione automatica dati' },
    { code: 'CONTRATTO_FIRMATO', label: 'Contratto Firmato', category: 'contrattuale' },
    { code: 'DOCUMENTO_IDENTITA', label: 'Documento di Identità', category: 'legale' },
    { code: 'CODICE_FISCALE', label: 'Codice Fiscale', category: 'legale' },
    { code: 'VISURA_CATASTALE', label: 'Visura Catastale', category: 'amministrativo' },
    { code: 'PLANIMETRIA', label: 'Planimetria', category: 'tecnico' },
    { code: 'BOLLETTA_ENERGIA', label: 'Bolletta Energia', category: 'amministrativo' },
    { code: 'PREVENTIVO', label: 'Preventivo', category: 'contrattuale' },
    { code: 'PROGETTO_TECNICO', label: 'Progetto Tecnico', category: 'tecnico' },
    { code: 'PERMESSO_COSTRUIRE', label: 'Permesso di Costruire', category: 'amministrativo' },
    { code: 'AUTORIZZAZIONE_GSE', label: 'Autorizzazione GSE', category: 'amministrativo' },
    { code: 'FOTO_SOPRALLUOGO', label: 'Foto Sopralluogo', category: 'tecnico' },
    { code: 'DICHIARAZIONE_CONFORMITA', label: 'Dichiarazione di Conformità', category: 'tecnico' },
    { code: 'CERTIFICATO_COLLAUDO', label: 'Certificato di Collaudo', category: 'tecnico' }
];

// Document row component
const DocumentItem = ({ doc, onEdit, onDelete, onMoveUp, onMoveDown, isFirst, isLast, mandatory }) => (
    <div className={`flex items-center gap-2 p-3 rounded-lg border ${
        mandatory ? 'bg-red-50/50 border-red-200' : 'bg-muted/30 border-border'
    }`}>
        <FileText className={`w-5 h-5 ${mandatory ? 'text-red-500' : 'text-muted-foreground'}`} />
        <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
                <code className="text-xs font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{doc.code}</code>
                <span className="text-sm font-medium truncate">{doc.label}</span>
                {doc.supports_extraction && (
                    <Badge variant="outline" className="text-xs bg-violet-50 text-violet-700 border-violet-300 flex items-center gap-1">
                        <Robot className="w-3 h-3" />
                        Estrazione AI
                    </Badge>
                )}
            </div>
            {doc.description && (
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{doc.description}</p>
            )}
        </div>
        <Badge variant={mandatory ? "destructive" : "outline"} className="text-xs flex-shrink-0">
            {mandatory ? 'Obbligatorio' : 'Opzionale'}
        </Badge>
        <div className="flex items-center gap-1 flex-shrink-0">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onMoveUp(doc)} disabled={isFirst}>
                <ArrowUp className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onMoveDown(doc)} disabled={isLast}>
                <ArrowDown className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(doc)}>
                <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => onDelete(doc)}>
                <Trash className="w-3.5 h-3.5" />
            </Button>
        </div>
    </div>
);

// Main component
export default function StepDocumentBuilder({ 
    requiredDocuments = [], 
    optionalDocuments = [], 
    onChange,
    readOnly = false
}) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingDoc, setEditingDoc] = useState(null);
    const [editingList, setEditingList] = useState('required'); // 'required' or 'optional'
    const [formData, setFormData] = useState({
        code: '',
        label: '',
        description: '',
        category: 'altro',
        minFiles: 1,
        maxFiles: 1,
        allowedTypes: [],
        maxSizeMB: 10,
        supports_extraction: false
    });

    const handleAddNew = (listType) => {
        setEditingDoc(null);
        setEditingList(listType);
        setFormData({
            code: '',
            label: '',
            description: '',
            category: 'altro',
            minFiles: 1,
            maxFiles: 1,
            allowedTypes: [],
            maxSizeMB: 10,
            supports_extraction: false
        });
        setDialogOpen(true);
    };

    const handleEdit = (doc, listType) => {
        setEditingDoc(doc);
        setEditingList(listType);
        setFormData({
            code: doc.code || '',
            label: doc.label || '',
            description: doc.description || '',
            category: doc.category || 'altro',
            minFiles: doc.minFiles || 1,
            maxFiles: doc.maxFiles || 1,
            allowedTypes: doc.allowedTypes || [],
            maxSizeMB: doc.maxSizeMB || 10,
            supports_extraction: doc.supports_extraction || false
        });
        setDialogOpen(true);
    };

    const handleSave = () => {
        const docData = {
            code: formData.code.toUpperCase().replace(/\s+/g, '_'),
            label: formData.label,
            description: formData.description,
            category: formData.category,
            minFiles: formData.minFiles,
            maxFiles: formData.maxFiles,
            allowedTypes: formData.allowedTypes,
            maxSizeMB: formData.maxSizeMB,
            mandatory: editingList === 'required',
            supports_extraction: formData.supports_extraction
        };

        let newRequired = [...requiredDocuments];
        let newOptional = [...optionalDocuments];

        if (editingDoc) {
            // Editing existing
            if (editingList === 'required') {
                const idx = newRequired.findIndex(d => d.code === editingDoc.code);
                if (idx >= 0) newRequired[idx] = docData;
            } else {
                const idx = newOptional.findIndex(d => d.code === editingDoc.code);
                if (idx >= 0) newOptional[idx] = docData;
            }
        } else {
            // Adding new
            if (editingList === 'required') {
                newRequired.push(docData);
            } else {
                newOptional.push(docData);
            }
        }

        onChange(newRequired, newOptional);
        setDialogOpen(false);
    };

    const handleDelete = (doc, listType) => {
        let newRequired = [...requiredDocuments];
        let newOptional = [...optionalDocuments];

        if (listType === 'required') {
            newRequired = newRequired.filter(d => d.code !== doc.code);
        } else {
            newOptional = newOptional.filter(d => d.code !== doc.code);
        }

        onChange(newRequired, newOptional);
    };

    const handleMoveUp = (doc, listType) => {
        const list = listType === 'required' ? [...requiredDocuments] : [...optionalDocuments];
        const idx = list.findIndex(d => d.code === doc.code);
        if (idx > 0) {
            [list[idx - 1], list[idx]] = [list[idx], list[idx - 1]];
            if (listType === 'required') {
                onChange(list, optionalDocuments);
            } else {
                onChange(requiredDocuments, list);
            }
        }
    };

    const handleMoveDown = (doc, listType) => {
        const list = listType === 'required' ? [...requiredDocuments] : [...optionalDocuments];
        const idx = list.findIndex(d => d.code === doc.code);
        if (idx < list.length - 1) {
            [list[idx], list[idx + 1]] = [list[idx + 1], list[idx]];
            if (listType === 'required') {
                onChange(list, optionalDocuments);
            } else {
                onChange(requiredDocuments, list);
            }
        }
    };

    const handleSelectCommonDoc = (commonDoc) => {
        setFormData({
            ...formData,
            code: commonDoc.code,
            label: commonDoc.label,
            category: commonDoc.category,
            description: commonDoc.description || '',
            supports_extraction: commonDoc.supports_extraction || false
        });
    };

    if (readOnly) {
        return (
            <div className="space-y-4">
                {requiredDocuments.length > 0 && (
                    <div>
                        <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                            <Warning className="w-4 h-4 text-red-500" />
                            Documenti Obbligatori ({requiredDocuments.length})
                        </Label>
                        <div className="space-y-2">
                            {requiredDocuments.map((doc, i) => (
                                <div key={doc.code || `req-${i}`} className="flex items-center gap-2 p-2 bg-red-50/50 border border-red-200 rounded">
                                    <FileText className="w-4 h-4 text-red-500" />
                                    <code className="text-xs font-mono text-muted-foreground">{doc.code}</code>
                                    <span className="flex-1 text-sm">{doc.label}</span>
                                    {doc.supports_extraction && (
                                        <Badge variant="outline" className="text-xs bg-violet-50 text-violet-700 border-violet-300 flex items-center gap-1">
                                            <Robot className="w-3 h-3" />
                                            Estrazione AI
                                        </Badge>
                                    )}
                                    <Badge variant="destructive" className="text-xs">Obbligatorio</Badge>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {optionalDocuments.length > 0 && (
                    <div>
                        <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                            <FileText className="w-4 h-4 text-muted-foreground" />
                            Documenti Facoltativi ({optionalDocuments.length})
                        </Label>
                        <div className="space-y-2">
                            {optionalDocuments.map((doc, i) => (
                                <div key={doc.code || `opt-${i}`} className="flex items-center gap-2 p-2 bg-muted/30 border rounded">
                                    <FileText className="w-4 h-4 text-muted-foreground" />
                                    <code className="text-xs font-mono text-muted-foreground">{doc.code}</code>
                                    <span className="flex-1 text-sm">{doc.label}</span>
                                    {doc.supports_extraction && (
                                        <Badge variant="outline" className="text-xs bg-violet-50 text-violet-700 border-violet-300 flex items-center gap-1">
                                            <Robot className="w-3 h-3" />
                                            Estrazione AI
                                        </Badge>
                                    )}
                                    <Badge variant="outline" className="text-xs">Opzionale</Badge>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {requiredDocuments.length === 0 && optionalDocuments.length === 0 && (
                    <div className="text-center py-6 text-muted-foreground">
                        <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>Nessun documento configurato</p>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Required Documents */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-medium flex items-center gap-2">
                        <Warning className="w-4 h-4 text-red-500" />
                        Documenti Obbligatori ({requiredDocuments.length})
                    </Label>
                    <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleAddNew('required')}
                        data-testid="add-required-doc"
                    >
                        <Plus className="w-4 h-4 mr-1" />
                        Aggiungi
                    </Button>
                </div>
                {requiredDocuments.length > 0 ? (
                    <div className="space-y-2">
                        {requiredDocuments.map((doc, i) => (
                            <DocumentItem
                                key={doc.code}
                                doc={doc}
                                mandatory={true}
                                onEdit={(d) => handleEdit(d, 'required')}
                                onDelete={(d) => handleDelete(d, 'required')}
                                onMoveUp={(d) => handleMoveUp(d, 'required')}
                                onMoveDown={(d) => handleMoveDown(d, 'required')}
                                isFirst={i === 0}
                                isLast={i === requiredDocuments.length - 1}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-4 border-2 border-dashed rounded-lg text-muted-foreground">
                        <p className="text-sm">Nessun documento obbligatorio</p>
                    </div>
                )}
            </div>

            {/* Optional Documents */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-medium flex items-center gap-2">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        Documenti Facoltativi ({optionalDocuments.length})
                    </Label>
                    <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleAddNew('optional')}
                        data-testid="add-optional-doc"
                    >
                        <Plus className="w-4 h-4 mr-1" />
                        Aggiungi
                    </Button>
                </div>
                {optionalDocuments.length > 0 ? (
                    <div className="space-y-2">
                        {optionalDocuments.map((doc, i) => (
                            <DocumentItem
                                key={doc.code}
                                doc={doc}
                                mandatory={false}
                                onEdit={(d) => handleEdit(d, 'optional')}
                                onDelete={(d) => handleDelete(d, 'optional')}
                                onMoveUp={(d) => handleMoveUp(d, 'optional')}
                                onMoveDown={(d) => handleMoveDown(d, 'optional')}
                                isFirst={i === 0}
                                isLast={i === optionalDocuments.length - 1}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-4 border-2 border-dashed rounded-lg text-muted-foreground">
                        <p className="text-sm">Nessun documento facoltativo</p>
                    </div>
                )}
            </div>

            {/* Add/Edit Document Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            {editingDoc ? 'Modifica Documento' : 'Aggiungi Documento'}
                            <Badge className="ml-2" variant={editingList === 'required' ? 'destructive' : 'outline'}>
                                {editingList === 'required' ? 'Obbligatorio' : 'Opzionale'}
                            </Badge>
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                        {/* Quick select from common types */}
                        {!editingDoc && (
                            <div className="space-y-2">
                                <Label className="text-sm">Seleziona tipo comune</Label>
                                <div className="flex flex-wrap gap-1.5">
                                    {COMMON_DOC_TYPES.slice(0, 8).map(common => (
                                        <Button
                                            key={common.code}
                                            variant="outline"
                                            size="sm"
                                            className="h-7 text-xs"
                                            onClick={() => handleSelectCommonDoc(common)}
                                        >
                                            {common.label}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Codice *</Label>
                                <Input
                                    value={formData.code}
                                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase().replace(/\s+/g, '_')})}
                                    placeholder="ES: CONTRATTO_FIRMATO"
                                    className="font-mono text-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Categoria</Label>
                                <Select 
                                    value={formData.category} 
                                    onValueChange={(v) => setFormData({...formData, category: v})}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {DOCUMENT_CATEGORIES.map(cat => (
                                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Nome Documento *</Label>
                            <Input
                                value={formData.label}
                                onChange={(e) => setFormData({...formData, label: e.target.value})}
                                placeholder="Es: Contratto Firmato"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Descrizione</Label>
                            <Input
                                value={formData.description}
                                onChange={(e) => setFormData({...formData, description: e.target.value})}
                                placeholder="Descrizione opzionale del documento..."
                            />
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label>Min. File</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={formData.minFiles}
                                    onChange={(e) => setFormData({...formData, minFiles: parseInt(e.target.value) || 1})}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Max. File</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={formData.maxFiles}
                                    onChange={(e) => setFormData({...formData, maxFiles: parseInt(e.target.value) || 1})}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Max MB</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={formData.maxSizeMB}
                                    onChange={(e) => setFormData({...formData, maxSizeMB: parseInt(e.target.value) || 10})}
                                />
                            </div>
                        </div>

                        {/* AI Extraction Toggle */}
                        <div className="flex items-center justify-between p-4 rounded-lg border bg-violet-50/50 border-violet-200">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-full bg-violet-100">
                                    <Robot className="w-5 h-5 text-violet-600" />
                                </div>
                                <div>
                                    <Label className="text-sm font-medium">Estrazione AI</Label>
                                    <p className="text-xs text-muted-foreground">
                                        Abilita l'estrazione automatica dei dati dal documento con GPT-4 Vision
                                    </p>
                                </div>
                            </div>
                            <Switch
                                checked={formData.supports_extraction}
                                onCheckedChange={(checked) => setFormData({...formData, supports_extraction: checked})}
                            />
                        </div>
                        
                        {formData.supports_extraction && (
                            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                                <Sparkle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                <div className="text-xs">
                                    <strong>Nota:</strong> L'estrazione AI funziona con documenti PDF strutturati. 
                                    I dati estratti dovranno essere verificati dall'operatore prima dell'applicazione al CRM.
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            Annulla
                        </Button>
                        <Button 
                            onClick={handleSave}
                            disabled={!formData.code || !formData.label}
                        >
                            {editingDoc ? 'Salva Modifiche' : 'Aggiungi Documento'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
